import React, { useState, useEffect, useMemo } from "react";
import { Parallax } from "react-scroll-parallax";

const Portfolio = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const projects = useMemo(
    () => [
      {
        title: "AVR SWARNA MAHAL Ltd. -AR Newspaper AD ",
        description: `We created an Augmented Reality (AR) campaign for AVR Jewellers Ltd., 
transforming traditional print ads into interactive experiences. By scanning a newspaper 
poster, users could view and engage with the advertisement in a whole new way. This 
campaign was launched across multiple states, bringing an immersive dimension to brand 
storytelling.`,
        image: "/images/portfolio/avr.png",
      },
      {
        title: "AGS ENTERTAINMENT -  G.O.A.T Filter",
        description: ` We launched an Instagram filter featuring the G.O.A.T. movie  and 'Greatest of All 
Time' logo, enabling fans to celebrate their favorite idols by creating engaging reels and 
stories. The filter gained traction, generated over 1,20,000 impressions and amplified the 
movie’s fan engagement. `,
        image: "/images/portfolio/GoatFilter.png",
      },
      {
        title: "AGS ENTERTAINMENT - SNAP WITH G.O.A.T.",
        description: `We created an exciting AR filter on Snapchat that allowed fans to click photos 
alongside actor Vijay in his iconic roles as Gandhi and Jeevan, along with director Venkat 
Prabhu. This unique experience gave fans a chance to capture memorable moments with their 
favorite stars. The filter received over 1,70,000 views, with more than 1,00,000 users actively 
engaged with it.`,
        image: "/images/portfolio/SnapwithGoat.png",
      },
      {
        title: "AGS ENTERTAINMENT - HOP ON A RIDE WITH G.O.A.T.",
        description: `We launched an AI powered face swap campaign allowing users to place their 
photo in actor Vijay’s seat on the back of a motorcycle scene from the movie. The campaign 
went viral, becoming a fan favorite and earning widespread media coverage as one of the 
most innovative AI-driven promotions generated 7M+ digital engagements, 2M+ website 
visits, and over 700K successful face swap images.`,
        image: "/images/portfolio/Faceswap.png",
      },
      {
        title: "AGS ENTERTAINMENT -  GUESS THE CHARACTER",
        description: `We created a playful Instagram filter that animated G.O.A.T. characters above 
users’ heads, randomly matching them with a character from the movie. This fun interaction 
brought the film’s characters to life, engaging fans in a new and exciting way. The filter 
reached over 1,60,000 users, with more than 90,000 snaps taken.`,
        image: "/images/portfolio/guesscharcter.png",
      },
      {
        title: "STUDIO GREEN - BE A VILLAIN OR HERO",
        description: `The Kanguva AI promotion lets fans swap their faces with the hero or villain, 
stepping into iconic scenes and creating shareable, personalized moments. The innovative 
campaign went viral, becoming a fan favorite and media sensation, setting a new benchmark 
for interactive movie promotions achieved 3M+ digital engagements, 750K+ website visits, 
and over 400K successful face swap images. `,
        image: "/images/portfolio/studiogreen.png",
      },
      {
        title: "VIJAY T.V. & JIO HOTSTAR - VIJAYNAVARATHIRI",
        description: `We collaborated with Vijay TV and JioHostar for a special Navarathiri campaign 
bringing 9 different Gods over 9 days as a live AR experience. For the first time, families 
across India could witness this divine celebration right in their living rooms through live 
television, blending tradition with technology.`,
        image: "/images/portfolio/Vijay_tv.png",
      },
      {
        title: "STARTUPTN - AI CHAT ASSISTANT FOR TNGSS EVENT ",
        description: `We worked with StartupTN, the leading startup ecosystem initiative by the 
Government of Tamil Nadu under the MSME department, to build an AI Chat Assistant for 
the TNGSS event. The assistant helps visitors to navigate the event, find the right stalls, get 
conference details, and instantly resolve their queries.`,
        image: "/images/portfolio/StartupTN.png",
      },
    ],
    []
  );

  const getSpeed = (desktopSpeed) =>
    isMobile ? desktopSpeed * 0.5 : desktopSpeed; // Enhanced: Increased mobile multiplier for more noticeable effect

  // Preload all project images once on mount to avoid delay during slide changes
  useEffect(() => {
    projects.forEach((project) => {
      const image = new Image();
      image.src = project.image;
    });
  }, [projects]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % projects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section
      className="relative px-4 sm:px-6 lg:px-8 overflow-hidden scroll-mt-0"
      style={{
        paddingTop: "clamp(16rem, 15vw, 12rem)",
        paddingBottom: "clamp(18rem, 15vw, 12rem)",
        scrollBehavior: "smooth",
      }}
    >
      {/* Left Glow Effect */}
      <Parallax
        speed={getSpeed(-30)}
        className="absolute pointer-events-none"
        style={{
          left: "clamp(-400px, -30vw, -200px)",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <div
          style={{
            width: "clamp(300px, 50vw, 600px)",
            height: "clamp(400px, 60vw, 800px)",
            background:
              "radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, rgba(6, 182, 212, 0.15) 40%, transparent 70%)",
            filter: "blur(30px)",
            opacity: 1.2,
          }}
        ></div>
      </Parallax>

      {/* Right Glow Effect */}
      <Parallax
        speed={getSpeed(30)}
        className="absolute pointer-events-none"
        style={{
          right: "clamp(-400px, -30vw, -200px)",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <div
          style={{
            width: "clamp(300px, 50vw, 600px)",
            height: "clamp(400px, 60vw, 800px)",
            background:
              "radial-gradient(circle, rgba(20, 184, 166, 0.15) 0%, rgba(20, 184, 166, 0.08) 40%, transparent 70%)",
            filter: "blur(10px)",
            opacity: 1.2,
          }}
        ></div>
      </Parallax>

      {/* Content Container */}
      <div className="w-full max-w-[1920px] mx-auto">
        <div className="px-0 sm:px-4 lg:px-8 xl:px-16">
          {/* Header */}
          <div className="text-left mb-8 sm:mb-12 md:mb-20">
            <Parallax
              speed={getSpeed(15)}
              translateY={isMobile ? [10, -10] : [20, -20]}
            >
              <h2
                className="font-normal mb-4 sm:mb-6 md:mb-8 bg-gradient-to-r from-[#009999] via-white to-white bg-clip-text text-transparent"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "clamp(2rem, 5vw, 4rem)",
                  lineHeight: "1.2",
                  letterSpacing: "0.08em",
                }}
              >
                Our Portfolio
              </h2>
            </Parallax>

            <Parallax
              speed={getSpeed(8)}
              translateY={isMobile ? [-10, 10] : [-20, 20]}
            >
              <p
                className="text-gray-400"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "clamp(14px, 2vw, 20px)",
                  lineHeight: "1.2",
                  letterSpacing: "0.08em",
                }}
              >
                Experience we have created
              </p>
            </Parallax>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Previous Button */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 transition-all hidden md:flex items-center justify-center"
            >
              <span className="text-white text-2xl">‹</span>
            </button>

            {/* Slide Content */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-6 sm:gap-8 lg:gap-12 px-0 lg:px-12 xl:px-20">
              {/* Image */}
              <Parallax
                speed={getSpeed(-8)}
                translateY={isMobile ? [8, -8] : [20, -20]}
              >
                <div
                  className="rounded-3xl overflow-hidden flex-shrink-0 relative w-full lg:w-auto"
                  style={{
                    maxWidth: "clamp(300px, 40vw, 458px)",
                    aspectRatio: "658 / 384",
                    borderRadius: "clamp(16px, 3vw, 26px)",
                  }}
                >
                  <img
                    src={projects[currentSlide].image}
                    alt={projects[currentSlide].title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="sync"
                    fetchpriority="high"
                  />
                </div>
              </Parallax>

              {/* Text Content */}
              <Parallax
                speed={getSpeed(12)}
                translateY={isMobile ? [-8, 8] : [-20, 20]}
              >
                <div
                  className="flex flex-col justify-between w-full lg:max-w-md xl:max-w-lg"
                  style={{
                    minHeight: "auto",
                  }}
                >
                  <div>
                    <h3
                      className="font-normal mb-3 sm:mb-4 md:mb-6"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "clamp(1.25rem, 3vw, 2rem)",
                        lineHeight: "1.2",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {projects[currentSlide].title}
                    </h3>
                    <p
                      className="text-gray-300 mb-4 sm:mb-6 md:mb-8"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "clamp(13px, 1.5vw, 16px)",
                        lineHeight: "1.5",
                        letterSpacing: "0.08em",
                      }}
                    >
                      {projects[currentSlide].description}
                    </p>
                  </div>
                </div>
              </Parallax>
            </div>

            {/* Next Button - Desktop */}
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 transition-all hidden md:flex items-center justify-center"
            >
              <span className="text-white text-2xl">›</span>
            </button>
          </div>

          {/* Mobile Navigation Buttons */}
          <div className="flex md:hidden justify-center gap-4 mt-6 sm:mt-8">
            <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center"
            >
              <span className="text-white text-2xl">‹</span>
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center"
            >
              <span className="text-white text-2xl">›</span>
            </button>
          </div>

          {/* Dots Indicator */}
          <Parallax
            speed={getSpeed(10)}
            translateY={isMobile ? [5, -5] : [10, -10]}
          >
            <div
              className="flex items-center justify-center mt-8 sm:mt-12"
              style={{
                gap: "clamp(4px, 1vw, 6px)",
              }}
            >
              {projects.map((_, index) => (
                <div
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className="cursor-pointer transition-all"
                  style={{
                    width:
                      index === currentSlide ? "clamp(30px, 6vw, 40px)" : "6px",
                    height: "6px",
                    backgroundColor:
                      index === currentSlide
                        ? "#FFFFFF"
                        : "rgba(255, 255, 255, 0.3)",
                    borderRadius: "100px",
                  }}
                />
              ))}
            </div>
          </Parallax>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
